import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EastRoundedIcon from "@mui/icons-material/EastRounded";
import { apiFetch } from "../../api/apiFetch";
import LoadingState from "../../components/common/LoadingState";

function actorName(moment) {
  return moment?.actor?.display_name || moment?.actor?.username || "TrustBud";
}

function commentName(comment) {
  return comment?.user?.display_name || comment?.user?.username || "TrustBud";
}

function momentIcon(type) {
  if (type === "reset_submitted" || type === "reset") return <RestartAltRoundedIcon fontSize="small" />;
  if (type === "proof_attached" || type === "proof" || type === "proof_acknowledged") return <ShieldOutlinedIcon fontSize="small" />;
  if (type === "group_all_in" || type === "both_reported" || type === "all_reported") return <GroupsRoundedIcon fontSize="small" />;
  if (type === "comeback" || type === "no_report_recovery") return <AutoAwesomeRoundedIcon fontSize="small" />;
  return <CheckCircleOutlineRoundedIcon fontSize="small" />;
}

function sourceRoute(moment) {
  if (moment?.connection_id) return `/network/person/${moment.connection_id}?source=moment`;
  if (moment?.agreement_key) return "/accountability";
  return "";
}

function replyPlaceholder(type) {
  if (type === "reset_submitted" || type === "reset") return "Send encouragement...";
  if (type === "proof_attached" || type === "proof") return "Ask about it or encourage...";
  return "Reply to this moment...";
}

function MomentCard({ moment, busy, onReact, onPublish, onOpenSource }) {
  const chips = [moment?.status_label, moment?.context_label, moment?.privacy_label].filter(Boolean);
  const canReact = Boolean(moment?.viewer_permissions?.can_react);
  const shareOptions = Array.isArray(moment?.share_options) ? moment.share_options : [];
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.4 },
        borderRadius: "22px",
        border: "1px solid rgba(188, 214, 235, 0.95)",
        bgcolor: "#fff",
        boxShadow: "0 14px 34px rgba(30, 41, 59, 0.06)",
      }}
    >
      <Stack spacing={1.8}>
        <Stack direction="row" spacing={1.3} alignItems="flex-start">
          <Avatar src={moment?.actor?.profile_image || ""} sx={{ width: 48, height: 48 }}>
            {actorName(moment).slice(0, 1).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 850, color: "#66758b" }}>
              {actorName(moment)}{moment?.relative_time ? ` · ${moment.relative_time}` : ""}
            </Typography>
            <Typography sx={{ mt: 0.35, fontSize: { xs: 22, sm: 26 }, lineHeight: 1.14, fontWeight: 950, color: "#13233d" }}>
              {moment?.title || "Accountability Moment"}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              bgcolor: "#eaf3ff",
              color: "#17345d",
              flexShrink: 0,
            }}
          >
            {momentIcon(moment?.type)}
          </Box>
        </Stack>

        {moment?.body ? (
          <Typography sx={{ color: "#56667a", fontSize: 15, lineHeight: 1.6, fontWeight: 600 }}>
            {moment.body}
          </Typography>
        ) : null}

        {chips.length ? (
          <Stack direction="row" spacing={0.8} sx={{ flexWrap: "wrap" }}>
            {chips.map((chip) => (
              <Chip
                key={chip}
                size="small"
                label={chip}
                sx={{
                  fontWeight: 850,
                  bgcolor: chip === moment?.privacy_label ? "#eef3f8" : "#f5faff",
                  color: chip === moment?.privacy_label ? "#56667a" : "#17345d",
                }}
              />
            ))}
          </Stack>
        ) : null}

        {moment?.is_share_suggested && shareOptions.length ? (
          <Box
            sx={{
              p: 1.5,
              borderRadius: "16px",
              border: "1px solid rgba(184, 216, 212, 0.95)",
              bgcolor: "#f7fbf8",
            }}
          >
            <Typography sx={{ fontSize: 15, fontWeight: 950, color: "#13233d" }}>
              Share this progress?
            </Typography>
            <Typography sx={{ mt: 0.35, color: "#56667a", fontSize: 13.5, lineHeight: 1.5 }}>
              Private details stay hidden. Choose who can see this Moment card in Activity.
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1.2, flexWrap: "wrap" }}>
              {shareOptions.map((option) => (
                <Button
                  key={option.key}
                  variant={option.key === "only_me" ? "outlined" : "contained"}
                  disabled={Boolean(busy)}
                  onClick={() => onPublish(option.key)}
                  sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}
                >
                  {option.label}
                </Button>
              ))}
            </Stack>
          </Box>
        ) : null}

        {moment?.reaction_summary?.length ? (
          <Stack direction="row" spacing={0.8} sx={{ flexWrap: "wrap" }}>
            {moment.reaction_summary.slice(0, 5).map((reaction) => (
              <Button
                key={reaction.reaction_type}
                size="small"
                variant={reaction.viewer_reacted ? "contained" : "outlined"}
                disabled={!canReact || Boolean(busy)}
                onClick={() => onReact(reaction.reaction_type)}
                sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850 }}
              >
                {reaction.label}{reaction.count ? ` ${reaction.count}` : ""}
              </Button>
            ))}
          </Stack>
        ) : null}

        {sourceRoute(moment) ? (
          <Button
            onClick={onOpenSource}
            endIcon={<EastRoundedIcon />}
            sx={{ alignSelf: "flex-start", borderRadius: 999, textTransform: "none", fontWeight: 900 }}
          >
            Open source
          </Button>
        ) : null}
      </Stack>
    </Paper>
  );
}

function CommentsCard({ moment, comments, draft, busy, onDraftChange, onSubmit, onDeleteComment }) {
  const canComment = Boolean(moment?.viewer_permissions?.can_comment);
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.2 },
        borderRadius: "20px",
        border: "1px solid rgba(231, 235, 244, 1)",
        bgcolor: "#fff",
      }}
    >
      <Stack spacing={1.4}>
        <Stack direction="row" spacing={0.9} alignItems="center">
          <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 20, color: "#17345d" }} />
          <Typography sx={{ fontSize: 18, fontWeight: 950, color: "#13233d" }}>
            Replies
          </Typography>
        </Stack>

        {comments.length ? (
          <Stack spacing={1}>
            {comments.map((comment) => (
              <Stack
                key={comment.id}
                direction="row"
                spacing={1}
                alignItems="flex-start"
                sx={{
                  p: 1.2,
                  borderRadius: "14px",
                  bgcolor: comment.is_viewer ? "#f7fbf8" : "#f8fbff",
                  border: "1px solid rgba(23, 52, 93, 0.08)",
                }}
              >
                <Avatar src={comment?.user?.profile_image || ""} sx={{ width: 32, height: 32, fontSize: 13, fontWeight: 900 }}>
                  {commentName(comment).slice(0, 1).toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Typography sx={{ fontSize: 12, fontWeight: 900, color: "#13233d", minWidth: 0 }}>
                      {comment.is_viewer ? "You" : commentName(comment)}
                    </Typography>
                    {comment.is_viewer ? (
                      <IconButton
                        size="small"
                        aria-label="Remove reply"
                        title="Remove reply"
                        disabled={Boolean(busy)}
                        onClick={() => onDeleteComment(comment.id)}
                        sx={{ width: 28, height: 28, color: "#66758b" }}
                      >
                        <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    ) : null}
                  </Stack>
                  <Typography sx={{ mt: 0.2, fontSize: 14, lineHeight: 1.55, color: "#56667a", whiteSpace: "pre-wrap" }}>
                    {comment.body}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Stack>
        ) : (
          <Typography sx={{ p: 1.5, borderRadius: "14px", bgcolor: "#f8fbff", color: "#66758b", fontWeight: 700 }}>
            No replies yet.
          </Typography>
        )}

        {canComment ? (
          <Stack spacing={1}>
            <TextField
              value={draft}
              onChange={(event) => onDraftChange(event.target.value)}
              placeholder={replyPlaceholder(moment?.type)}
              multiline
              minRows={3}
              inputProps={{ maxLength: 280 }}
              fullWidth
            />
            <Button
              variant="contained"
              disabled={!draft.trim() || Boolean(busy)}
              onClick={onSubmit}
              sx={{ alignSelf: "flex-end", borderRadius: 999, textTransform: "none", fontWeight: 900 }}
            >
              {busy === "comment" ? "Sending..." : "Send reply"}
            </Button>
          </Stack>
        ) : null}
      </Stack>
    </Paper>
  );
}

export default function MomentDetailPage() {
  const navigate = useNavigate();
  const { momentId } = useParams();
  const [moment, setMoment] = React.useState(null);
  const [comments, setComments] = React.useState([]);
  const [draft, setDraft] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState("");
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");

  const load = React.useCallback(async () => {
    if (!momentId) {
      setError("Moment not found.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await apiFetch(`/moments/${momentId}/`);
      setMoment(data?.moment || null);
      setComments(Array.isArray(data?.comments) ? data.comments : []);
    } catch (err) {
      setError(err?.message || "This moment is no longer available.");
      setMoment(null);
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, [momentId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const react = React.useCallback(async (reactionType) => {
    if (!moment?.id || busy) return;
    setBusy(`reaction:${reactionType}`);
    setNotice("");
    try {
      const result = await apiFetch(`/moments/${moment.id}/reactions/`, {
        method: "POST",
        body: JSON.stringify({ reaction_type: reactionType }),
      });
      if (result?.moment) setMoment(result.moment);
    } catch (err) {
      setNotice(err?.message || "Unable to save that reaction.");
    } finally {
      setBusy("");
    }
  }, [busy, moment?.id]);

  const submitComment = React.useCallback(async () => {
    const body = draft.trim();
    if (!moment?.id || !body || busy) return;
    setBusy("comment");
    setNotice("");
    try {
      const result = await apiFetch(`/moments/${moment.id}/comments/`, {
        method: "POST",
        body: JSON.stringify({ body }),
      });
      if (result?.comment) setComments((current) => [...current, result.comment]);
      if (result?.moment) setMoment(result.moment);
      setDraft("");
    } catch (err) {
      setNotice(err?.message || "Unable to add that reply.");
    } finally {
      setBusy("");
    }
  }, [busy, draft, moment?.id]);

  const deleteComment = React.useCallback(async (commentId) => {
    if (!moment?.id || !commentId || busy) return;
    setBusy(`delete-comment:${commentId}`);
    setNotice("");
    try {
      await apiFetch(`/moments/${moment.id}/comments/${commentId}/`, {
        method: "DELETE",
      });
      setComments((current) => current.filter((comment) => comment.id !== commentId));
      setMoment((current) =>
        current
          ? {
              ...current,
              comment_count: Math.max(0, Number(current.comment_count || 0) - 1),
            }
          : current
      );
      setNotice("Reply removed.");
    } catch (err) {
      setNotice(err?.message || "Unable to remove that reply.");
    } finally {
      setBusy("");
    }
  }, [busy, moment?.id]);

  const publish = React.useCallback(async (visibility) => {
    if (!moment?.id || busy) return;
    setBusy(`publish:${visibility}`);
    setNotice("");
    try {
      const result = await apiFetch(`/moments/${moment.id}/publish/`, {
        method: "POST",
        body: JSON.stringify({
          visibility,
          action: visibility === "only_me" ? "keep_private" : "publish",
        }),
      });
      if (result?.moment) setMoment(result.moment);
      setNotice(result?.message || "Moment updated.");
    } catch (err) {
      setNotice(err?.message || "Unable to update this Moment.");
    } finally {
      setBusy("");
    }
  }, [busy, moment?.id]);

  const openSource = React.useCallback(() => {
    const route = sourceRoute(moment);
    if (route) navigate(route);
  }, [moment, navigate]);

  if (loading) {
    return (
      <LoadingState
        title="Opening Moment"
        body="Loading the progress card, reactions, and replies."
        steps={["Progress card", "Reactions", "Replies"]}
        rows={2}
        compact
        sx={{ width: "100%", maxWidth: 520, mx: "auto", mt: { xs: 2, sm: 4 } }}
      />
    );
  }

  return (
    <Box sx={{ maxWidth: 900 }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1.1} alignItems="center">
          <IconButton onClick={() => navigate("/activity?tab=moments")} sx={{ border: "1px solid rgba(188, 214, 235, 0.95)" }}>
            <ArrowBackRoundedIcon />
          </IconButton>
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#66758b", textTransform: "uppercase", letterSpacing: 0.5 }}>
              Moment
            </Typography>
            <Typography sx={{ fontSize: 24, lineHeight: 1.15, fontWeight: 950, color: "#13233d" }}>
              Activity detail
            </Typography>
          </Box>
        </Stack>

        {error ? (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={() => navigate("/activity?tab=moments")}>
                Back
              </Button>
            }
          >
            {error}
          </Alert>
        ) : null}

        {notice ? (
          <Alert
            severity={/unable|cannot|not/i.test(notice) ? "error" : "success"}
            onClose={() => setNotice("")}
          >
            {notice}
          </Alert>
        ) : null}

        {moment ? (
          <>
            <MomentCard
              moment={moment}
              busy={busy}
              onReact={react}
              onPublish={publish}
              onOpenSource={openSource}
            />
            <CommentsCard
              moment={moment}
              comments={comments}
              draft={draft}
              busy={busy}
              onDraftChange={setDraft}
              onSubmit={submitComment}
              onDeleteComment={deleteComment}
            />
          </>
        ) : !error ? (
          <Paper elevation={0} sx={{ p: 3, borderRadius: "20px", border: "1px solid rgba(231, 235, 244, 1)" }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography sx={{ color: "#56667a", fontWeight: 700 }}>
                Loading Moment...
              </Typography>
            </Stack>
          </Paper>
        ) : null}
      </Stack>
    </Box>
  );
}
